import { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { isCompactApplicationType } from '../utils/constants';

function GeoMapFrame({ lat, lng }: Readonly<{ lat: number; lng: number }>) {
    const frameRef = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const openInNewTab = isCompactApplicationType();

    useEffect(() => {
        const onChange = () => setIsFullscreen(document.fullscreenElement === frameRef.current);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const toggleFullscreen = () => {
        if (openInNewTab) {
            window.open(`https://www.google.com/maps?q=${lat},${lng}&z=15`, '_blank', 'noopener,noreferrer');
            return;
        }
        const request = isFullscreen ? document.exitFullscreen() : frameRef.current?.requestFullscreen();
        request?.catch(() => undefined);
    };

    const embedUrl = `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
    let viewLabel = 'Full view';
    if (openInNewTab) viewLabel = 'Open in new tab';
    else if (isFullscreen) viewLabel = 'Exit full view';

    return (
        <div className="my-2.5 rounded-xl overflow-hidden border-2 border-default-200 bg-default-100">
            <div ref={frameRef} className={`relative w-full bg-content1 ${isFullscreen ? 'h-screen' : 'h-[250px]'}`}>
                <iframe
                    title={`Map ${lat}, ${lng}`}
                    src={embedUrl}
                    className="w-full h-full border-0"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                />
                <button
                    type="button"
                    onClick={toggleFullscreen}
                    title={viewLabel}
                    aria-label={viewLabel}
                    className="absolute top-2 right-2 z-10 flex items-center justify-center w-8 h-8 rounded-md bg-content1 text-foreground border border-divider shadow-md hover:bg-default-100"
                >
                    <Icon icon={isFullscreen ? 'material-symbols:fullscreen-exit' : 'material-symbols:fullscreen'} className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
}

/** Same map the legacy claim-score attachment uses. Coordinates come from `modified_GPS`. */
export const renderMap = (imageScore: any) => {
    const lat = imageScore?.modified_GPS?.latitude || 40.573339204027945;
    const lng = imageScore?.modified_GPS?.longitude || -74.11933042781544;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

    return <GeoMapFrame lat={lat} lng={lng} />;
};
